/**
 * Поле отправки сообщения.
 *
 * Самый ответственный компонент интерфейса. Реализует §28 ТЗ:
 * пользователь должен ЧЁТКО видеть, от какого Telegram-аккаунта уйдёт
 * сообщение, и случайная отправка через другой аккаунт при переключении между
 * чатами недопустима.
 *
 * Три механизма защиты, каждый нужен отдельно:
 *
 *  1. Аккаунт показан рядом с полем ввода постоянно, а не в заголовке экрана.
 *     Взгляд человека при наборе текста находится здесь.
 *  2. При смене чата черновик и состояние «готов отправить» сбрасываются.
 *     Текст, набранный для одного клиента, не должен уйти другому.
 *  3. `telegramAccountId` уходит на сервер и сверяется там. Ответ 409 означает,
 *     что представление интерфейса разошлось с сервером, — показываем это
 *     явно и НЕ отправляем повторно автоматически.
 */

import { useEffect, useRef, useState } from 'react';
import type { AnyTelegramAccountDto } from '@tw/shared-types';
import { ApiError, newIdempotencyKey, type MediaKind, type QuickReplyDto } from '../lib/api.js';
import { RichText } from './RichText.tsx';
import { RichTextInput, type RichTextInputHandle, type RichValue } from './RichTextInput.tsx';
import { QuickReplyThumbs } from './QuickReplyThumbs.tsx';
import { useSession } from '../state/session.tsx';
import { accountLabel } from './accountLabel.js';
import { ClipIcon, LockIcon, SendIcon, TrashIcon } from './icons.tsx';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const STATE_DOT: Record<string, string> = {
  CONNECTED: 'bg-[var(--ok)]',
  RECONNECTING: 'bg-[var(--warn)]',
  AUTHORIZATION_REQUIRED: 'bg-[var(--warn)]',
  ERROR: 'bg-destructive',
  DISCONNECTED: 'bg-destructive',
};
import { QuickReplyPicker } from './QuickReplyPicker.tsx';
import { StickerPicker } from './StickerPicker.tsx';
import { VoiceRecorder } from './VoiceRecorder.tsx';
import { VideoNoteRecorder } from './VideoNoteRecorder.tsx';
import { EmojiPicker } from './EmojiPicker.tsx';
import { useIsMobile } from '../lib/useMedia.ts';

interface ComposerProps {
  chatId: string;
  /** Нужен, чтобы не предлагать самоуничтожение там, где оно не сработает. */
  chatType: string;
  account: AnyTelegramAccountDto;
  replyTo?: { telegramMessageId: string; preview: string } | null;
  /** id чата, В КОТОРЫЙ ушло сообщение, — к концу отправки открыт может быть другой. */
  onSent: (chatId: string) => void;
  onCancelReply?: () => void;
}

export function Composer({
  chatId,
  chatType,
  account,
  replyTo,
  onSent,
  onCancelReply,
}: ComposerProps) {
  const { api, can } = useSession();

  const [rich, setRich] = useState<RichValue>({ text: '', entities: [] });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staleAccount, setStaleAccount] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [selfDestruct, setSelfDestruct] = useState<number | null>(null);
  /**
   * Заготовка, которая уйдёт целиком.
   *
   * Простая текстовая заготовка по-прежнему вставляется в поле — её можно
   * дописать. Заготовка с вложениями или премиальными эмодзи в поле не
   * помещается: у них нет текстового вида. Она встаёт сюда, показывается
   * как есть и уходит кнопкой отправки отдельным маршрутом.
   */
  const [template, setTemplate] = useState<QuickReplyDto | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  /**
   * Ключ идемпотентности живёт до успешной отправки.
   *
   * При ретрае после обрыва связи уходит ТОТ ЖЕ ключ — сервер распознаёт повтор
   * и не создаёт второе сообщение. Новый ключ генерируется только после того,
   * как предыдущее сообщение ушло.
   */
  const idempotencyKey = useRef(newIdempotencyKey());

  /** Нужен, чтобы вставлять заготовку и эмодзи в позицию курсора, а не в конец. */
  const richRef = useRef<RichTextInputHandle>(null);
  const mobile = useIsMobile();
  const [toolsOpen, setToolsOpen] = useState(false);

  /**
   * Вставка фрагмента в позицию курсора.
   *
   * Общая для заготовок и эмодзи: обе не должны затирать набранное. Менеджер
   * часто пишет обращение сам и подставляет заготовку следом, а отмены у нас
   * нет.
   */
  const insertAtCursor = (snippet: string): void => {
    richRef.current?.insertText(snippet);
  };

  /*
   * Какой чат открыт сейчас — для отправок, которые заканчиваются позже.
   *
   * Отправка видит chatId на момент нажатия. Если за время запроса менеджер
   * перешёл в другой чат, очистка после успеха стирала уже НОВЫЙ черновик —
   * текст, набранный для другого клиента, пропадал молча.
   */
  const currentChatId = useRef(chatId);
  useEffect(() => {
    currentChatId.current = chatId;
  }, [chatId]);
  const stillOpen = (sentTo: string): boolean => currentChatId.current === sentTo;

  // Смена чата или аккаунта обнуляет всё состояние отправки.
  useEffect(() => {
    richRef.current?.clear();
    setRich({ text: '', entities: [] });
    setError(null);
    setStaleAccount(false);
    // Вложение сбрасывается вместе с черновиком: файл, выбранный для одного
    // клиента, не должен уйти другому (§28 ТЗ, та же логика, что и для текста).
    setFile(null);
    setTemplate(null);
    idempotencyKey.current = newIdempotencyKey();
  }, [chatId, account.id]);

  const pickTemplate = (reply: QuickReplyDto): void => {
    const rich = (reply.attachments?.length ?? 0) > 0 || (reply.entities?.length ?? 0) > 0;
    if (!rich) {
      insertAtCursor(reply.text);
      return;
    }
    setTemplate(reply);
    setFile(null);
  };

  const allowed = can('telegram.message.send');

  if (!allowed) {
    // Стажёр видит переписку, но не поле ввода. Это UX; запрет держит сервер.
    return (
      <div className="flex flex-none items-center justify-center gap-1.5 border-t border-border/60 bg-card/85 py-4 text-sm text-muted-foreground backdrop-blur-xl">
        <LockIcon className="size-4" />
        <span>Режим только для чтения</span>
      </div>
    );
  }

  /** Есть ли что отправлять: текст, выбранный файл либо заготовка. */
  const hasPayload = rich.text.trim().length > 0 || file !== null || template !== null;

  const send = async (): Promise<void> => {
    const value = rich.text.trim();
    // Сущности (премиальные эмодзи) отправляем только вместе с непустым текстом.
    const entities = rich.entities.length > 0 ? rich.entities : undefined;
    if (sending) return;
    const sentTo = chatId;

    if (template) {
      setSending(true);
      setError(null);
      try {
        await api.sendQuickReply(chatId, {
          telegramAccountId: account.id,
          quickReplyId: template.id,
          idempotencyKey: idempotencyKey.current,
          ...(replyTo ? { replyToTelegramMessageId: replyTo.telegramMessageId } : {}),
        });
        if (stillOpen(sentTo)) {
          setTemplate(null);
          idempotencyKey.current = newIdempotencyKey();
          onCancelReply?.();
        }
        onSent(sentTo);
      } catch (err) {
        // Ошибку чужого чата в новом не показываем: «аккаунт изменился»
        // заблокировал бы отправку там, где всё в порядке.
        if (!stillOpen(sentTo)) return;
        if (err instanceof ApiError && err.isConflict) {
          setStaleAccount(true);
          setError('Аккаунт чата изменился с момента загрузки. Обновите список и отправьте заново.');
        } else {
          setError(err instanceof Error ? err.message : 'Не удалось отправить заготовку');
        }
      } finally {
        setSending(false);
      }
      return;
    }
    // Без файла пустое сообщение отправлять нечего; с файлом текст
    // необязателен — он становится подписью.
    if (!value && !file) return;

    setSending(true);
    setError(null);

    try {
      if (file) {
        await api.sendMedia(chatId, file, {
          telegramAccountId: account.id,
          kind: detectKind(file),
          ...(value ? { caption: value } : {}),
          ...(selfDestruct ? { selfDestructSeconds: selfDestruct } : {}),
        });

        if (stillOpen(sentTo)) {
          richRef.current?.clear();
          setRich({ text: '', entities: [] });
          setFile(null);
          setSelfDestruct(null);
          if (fileRef.current) fileRef.current.value = '';
        }
        onSent(sentTo);
        return;
      }

      await api.sendMessage(chatId, {
        // Явно подтверждаем, от какого аккаунта отправляем.
        telegramAccountId: account.id,
        text: value,
        idempotencyKey: idempotencyKey.current,
        ...(entities ? { entities } : {}),
        ...(replyTo ? { replyToTelegramMessageId: replyTo.telegramMessageId } : {}),
      });

      if (stillOpen(sentTo)) {
        richRef.current?.clear();
        setRich({ text: '', entities: [] });
        idempotencyKey.current = newIdempotencyKey();
        onCancelReply?.();
      }
      onSent(sentTo);
    } catch (err) {
      if (!stillOpen(sentTo)) return;
      if (err instanceof ApiError && err.isConflict) {
        // Сервер сообщил: выбранный аккаунт не совпадает с аккаунтом чата.
        // Текст сохраняем, отправку блокируем, требуем обновить данные.
        // Автоматически повторить нельзя — это и была бы отправка не от того
        // лица, которую §28 ТЗ запрещает.
        setStaleAccount(true);
        setError(
          'Аккаунт чата изменился с момента загрузки. Обновите список и отправьте заново.',
        );
      } else if (err instanceof ApiError && err.isForbidden) {
        setError('Недостаточно прав для отправки в этот чат');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось отправить сообщение');
      }
    } finally {
      setSending(false);
    }
  };

  const tools = (
    <>
        <Button
          variant="ghost"
          className="size-[42px] flex-none rounded-full"
          disabled={sending || staleAccount}
          aria-label="Прикрепить файл"
          title="Прикрепить файл"
          onClick={() => fileRef.current?.click()}
        >
          <ClipIcon className="size-[18px]" />
        </Button>

        <VideoNoteRecorder
          disabled={sending || staleAccount}
          onRecorded={async (note, side) => {
            const sentTo = chatId;
            setError(null);
            try {
              await api.sendMedia(sentTo, note, {
                telegramAccountId: account.id,
                kind: 'videoNote',
                // Подписи у кружочка нет: Telegram её не показывает, и сервер
                // отвергает попытку её передать.
                videoNoteLength: side,
              });
              onSent(sentTo);
            } catch (err) {
              if (stillOpen(sentTo))
                setError(err instanceof Error ? err.message : 'Не удалось отправить видеосообщение');
              throw err;
            }
          }}
        />

        <StickerPicker
          chatId={chatId}
          disabled={sending || staleAccount}
          onPick={async (fileId) => {
            const sentTo = chatId;
            setError(null);
            try {
              await api.sendSticker(sentTo, {
                telegramAccountId: account.id,
                fileId,
                ...(replyTo ? { replyToTelegramMessageId: replyTo.telegramMessageId } : {}),
              });
              if (stillOpen(sentTo)) onCancelReply?.();
              onSent(sentTo);
            } catch (err) {
              if (stillOpen(sentTo))
                setError(err instanceof Error ? err.message : 'Не удалось отправить стикер');
            }
          }}
        />

        <QuickReplyPicker chatId={chatId} disabled={sending || staleAccount} onPick={pickTemplate} />
    </>
  );

  return (
    /*
     * Композер лежит НАД лентой отдельной поверхностью со стеклом, а не
     * приклеен к низу линией. Так видно, что переписка продолжается под ним,
     * и панель не выглядит обрубающей ленту планкой.
     *
     * На телефоне — отступ под полоску «Домой» iPhone: в открытой переписке
     * нижней панели разделов нет, и композер стоит у самого края экрана.
     */
    <div
      className={cn('flex-none border-t border-border/60 bg-card/85 py-2.5 backdrop-blur-xl', mobile ? 'px-2' : 'px-3.5')}
      style={mobile ? { paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)' } : undefined}
    >
      {replyTo && (
        <div className="mb-2 flex items-center gap-2 border-l-2 border-primary pl-2.5 text-xs text-muted-foreground">
          <span className="flex-1 truncate">{replyTo.preview}</span>
          <button type="button" onClick={onCancelReply} aria-label="Отменить ответ">
            ×
          </button>
        </div>
      )}

      {/*
        Индикатор аккаунта. Намеренно не сворачивается и не прячется при
        наборе: он должен быть в поле зрения именно в момент набора текста.
      */}
      <div
        className={cn(
          'mb-2 flex items-center gap-1.5 text-xs',
          staleAccount ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        <span>Отправка от</span>
        <strong className={staleAccount ? 'text-destructive' : 'font-semibold text-foreground'}>
          {accountLabel(account)}
        </strong>
        <span
          className={cn('size-[7px] rounded-full', STATE_DOT[account.connectionState] ?? 'bg-muted-foreground')}
          title={connectionHint(account.connectionState)}
        />
      </div>

      {template && (
        <div className="mb-2 flex items-start gap-2 rounded-xl bg-muted px-2.5 py-2 text-sm">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Заготовка · {template.title}
            </span>
            {template.text && (
              <span className="line-clamp-4 whitespace-pre-wrap text-[13.5px] leading-relaxed">
                <RichText text={template.text} entities={template.entities ?? []} chatId={chatId} />
              </span>
            )}
            {template.attachments && template.attachments.length > 0 && (
              <QuickReplyThumbs replyId={template.id} attachments={template.attachments} size="md" />
            )}
          </div>
          <button
            type="button"
            aria-label="Убрать заготовку"
            className="flex-none rounded-lg p-1 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
            onClick={() => setTemplate(null)}
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      )}

      {file && (
        <div className="mb-2 flex items-center gap-2 rounded-xl bg-muted px-2.5 py-2 text-sm">
          <ClipIcon className="size-4 flex-none text-muted-foreground" />
          <span className="truncate">{file.name}</span>
          <span className="flex-none text-xs text-muted-foreground">{formatSize(file.size)}</span>
          <button
            type="button"
            aria-label="Убрать вложение"
            className="ml-auto flex-none rounded-lg p-1 text-muted-foreground transition-colors hover:bg-destructive/15 hover:text-destructive"
            onClick={() => {
              setFile(null);
              setSelfDestruct(null);
              if (fileRef.current) fileRef.current.value = '';
            }}
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      )}

      {/*
        Самоуничтожение предлагается только там, где Telegram его исполнит:
        фотографии и видео в личной переписке. В группе поле было бы
        проигнорировано, и отправитель остался бы уверен, что послал
        исчезающее сообщение.
      */}
      {file && chatType === 'private' && ['photo', 'video'].includes(detectKind(file)) && (
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              className="accent-[var(--accent-fill)]"
              checked={selfDestruct !== null}
              onChange={(e) => setSelfDestruct(e.target.checked ? 10 : null)}
            />
            Исчезающее
          </label>

          {selfDestruct !== null && (
            <>
              <select
                value={selfDestruct}
                onChange={(e) => setSelfDestruct(Number(e.target.value))}
                className="rounded-lg border border-border bg-background px-2 py-1 text-xs outline-none focus:border-ring"
              >
                <option value={5}>5 секунд</option>
                <option value={10}>10 секунд</option>
                <option value={30}>30 секунд</option>
                <option value={60}>1 минута</option>
              </select>
              <span className="opacity-70">отсчёт пойдёт с момента, когда получатель откроет</span>
            </>
          )}
        </div>
      )}

      {mobile && toolsOpen && <div className="mb-2 flex items-center gap-1">{tools}</div>}

      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const picked = e.target.files?.[0] ?? null;
            setError(null);
            if (picked && picked.size > MAX_FILE_BYTES) {
              setError(`Файл больше ${MAX_FILE_BYTES / 1024 / 1024} МБ`);
              e.target.value = '';
              return;
            }
            setFile(picked);
          }}
        />

        {/*
          Телефон: вложение, кружок, стикеры и заготовки — под одной «+»,
          иначе четыре кнопки съедают половину ширины поля ввода.
        */}
        {mobile ? (
          <Button
            variant="ghost"
            className={cn('size-[42px] flex-none rounded-full text-[22px] font-light transition-transform', toolsOpen && 'rotate-45 text-brand')}
            disabled={sending || staleAccount}
            aria-label={toolsOpen ? 'Скрыть вложения' : 'Вложения, стикеры, заготовки'}
            aria-expanded={toolsOpen}
            onClick={() => setToolsOpen((v) => !v)}
          >
            +
          </Button>
        ) : (
          tools
        )}

        <div className="relative flex min-w-0 flex-1">
          <RichTextInput
            ref={richRef}
            value={rich}
            onChange={setRich}
            onEnter={() => void send()}
            ariaLabel="Написать сообщение"
            placeholder={template ? 'Заготовка уйдёт как есть — нажмите отправить' : 'Написать сообщение…'}
            disabled={sending || staleAccount || template !== null}
            className="min-h-[24px] rounded-[20px] border border-border bg-background py-2.5 pl-4 pr-12 text-[15px] leading-relaxed transition-colors focus-within:border-ring"
          />

          {/* Кнопка внутри поля, а не рядом с ним: так она не растягивает
              строку композера и остаётся на месте при росте текста. */}
          <EmojiPicker
            disabled={sending || staleAccount}
            onPick={insertAtCursor}
            onPickCustom={(id, emoji, src) => richRef.current?.insertCustomEmoji(id, emoji, src)}
            accountId={account.id}
            accountIsPremium={account.masked ? null : account.isPremium}
          />
        </div>

        {/*
          Кнопка отправки появляется, только когда есть что отправлять.
          Так устроен Telegram, и это не украшательство: пустая активная
          кнопка «отправить» в мессенджере читается как приглашение нажать,
          а нажатие ничего не делает. Пока отправлять нечего, её место
          занимают эмодзи и запись голосового — тоже как в оригинале.
        */}
        {hasPayload ? (
          <Button
            size="icon-lg"
            className="size-[42px] flex-none rounded-full"
            onClick={() => void send()}
            disabled={sending || staleAccount}
            aria-label={sending ? 'Отправка сообщения' : 'Отправить сообщение'}
            title="Отправить (Enter)"
          >
            <SendIcon className="size-[18px]" />
          </Button>
        ) : (
          <VoiceRecorder
            disabled={sending || staleAccount}
            onRecorded={async (voice, seconds) => {
              const sentTo = chatId;
              setError(null);
              try {
                await api.sendMedia(sentTo, voice, {
                  telegramAccountId: account.id,
                  kind: 'voice',
                  durationSeconds: seconds,
                });
                onSent(sentTo);
              } catch (err) {
                // Ошибку показываем здесь, а запись остаётся в композере:
                // заново наговаривать то же самое из-за сетевого сбоя обидно.
                if (stillOpen(sentTo))
                  setError(err instanceof Error ? err.message : 'Не удалось отправить голосовое');
                throw err;
              }
            }}
          />
        )}
      </div>

      {error && (
        <div className="mt-2 flex items-center gap-2 text-sm text-destructive" role="alert">
          {error}
          {staleAccount && (
            <Button size="xs" variant="outline" onClick={() => window.location.reload()}>
              Обновить
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function connectionHint(state: string): string {
  switch (state) {
    case 'CONNECTED':
      return 'Аккаунт подключён';
    case 'RECONNECTING':
      return 'Переподключение — сообщение может уйти с задержкой';
    case 'AUTHORIZATION_REQUIRED':
      return 'Требуется авторизация аккаунта';
    case 'DISCONNECTED':
      return 'Аккаунт отключён';
    default:
      return 'Ошибка подключения';
  }
}

/**
 * Потолок размера на клиенте.
 *
 * Дублирует ограничение сервиса намеренно: сказать «слишком большой» до
 * загрузки честнее, чем гонять сто мегабайт по каналу пользователя ради
 * отказа в конце.
 */
const MAX_FILE_BYTES = 100 * 1024 * 1024;

/**
 * Как показать вложение получателю.
 *
 * Решение принимается по MIME, но это именно догадка о намерении: снимок,
 * отправленный документом, теряет предпросмотр, а скриншот интерфейса,
 * отправленный фотографией, теряет резкость из-за пережатия Telegram. Выбран
 * первый вариант как более частый; при необходимости здесь появится
 * переключатель в интерфейсе.
 */
function detectKind(file: File): MediaKind {
  const type = file.type;

  // GIF отправляется анимацией, иначе Telegram покажет его статичной картинкой.
  if (type === 'image/gif') return 'animation';
  if (type.startsWith('image/')) return 'photo';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'document';
}

function formatSize(bytes: number): string {
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}
